import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import useQuickContract from './hooks/useQuickContract';
import { Checkbox, ListItemText } from '@mui/material';

function QuickContract() {
  const {
    formData,
    errors,
    handleChange,
    handleSubmit,
    resetForm,
    products,
    destinationName,
    customerName,
    handleQuickContract,
    successMessage,
    openSuccessDialog,
    setOpenSuccessDialog,
  } = useQuickContract();

  const history = useHistory();

  const contractTypes = ['Contract Haulage'];
  const destinations = ['Site A', 'Site B', 'Site C', 'Warehouse 1', 'Warehouse 2'];

  return (
    <>
      {' '}
      <Dialog open={openSuccessDialog} onClose={() => setOpenSuccessDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600, fontSize: 22 }}>Quick Contract Created Successfully</DialogTitle>

        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Site Assessment ID
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {successMessage?.id}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Site Reference
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {successMessage?.ref}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                WH Contract Number
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'green' }}>
                {successMessage?.contract}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={() => setOpenSuccessDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <div style={{ width: '100%', height: 'calc(100vh - 150px)', position: 'relative' }}>
        <div>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Quick Contract</h2>
            <Box display="flex" alignItems="center" gap={3}>
              <Button variant="contained" color="primary" onClick={() => history.push('/')}>
                Home
              </Button>
            </Box>
          </Box>
          <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Quick Contract Form
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* Contract Type */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      required
                      label="What Type of Contract was won?"
                      value={formData.contractType}
                      onChange={handleChange('contractType')}
                      error={!!errors.contractType}
                      helperText={errors.contractType}
                      size="small">
                      {contractTypes.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Description (Site ref)"
                      value={formData.description}
                      onChange={handleChange('description')}
                      size="small"
                    />
                  </Grid>

                  {/* Drop Pin Location */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Key Drop Pin Locations (Share Drop pin Link in Google)"
                      value={formData.dropPinLocation}
                      onChange={handleChange('dropPinLocation')}
                      placeholder="Paste Google Maps link here"
                      size="small"
                    />
                  </Grid>

                  {/* Product */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Product"
                      value={formData.product || []}
                      onChange={handleChange('product')}
                      SelectProps={{
                        multiple: true,
                        renderValue: selected => selected.join(', '),
                      }}
                      size="small">
                      {products.map(option => (
                        <MenuItem key={option} value={option}>
                          <Checkbox checked={formData.product.includes(option)} />
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Entrance */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Entrance"
                      value={formData.entrance}
                      onChange={handleChange('entrance')}
                      placeholder="Auto-filled from Drop Pin Location"
                      size="small"
                    />
                  </Grid>

                  {/* Destination */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Destination"
                      value={formData.destination}
                      onChange={handleChange('destination')}
                      size="small">
                      {destinationName.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Leeway */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Leeway (Radius in meters)"
                      value={formData.leeway}
                      onChange={handleChange('leeway')}
                      InputProps={{ inputProps: { min: 0 } }}
                      size="small"
                    />
                  </Grid>

                  {/* Start Date */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={handleChange('startDate')}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>

                  {/* Finish Date */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Finish Date"
                      value={formData.finishDate}
                      onChange={handleChange('finishDate')}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="h6" gutterBottom sx={{ mt: 1.5, fontWeight: 500 }}>
                      Customer Information
                    </Typography>
                  </Grid>

                  {/* Customer Details */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Customer Details"
                      value={formData.customerDetails}
                      onChange={handleChange('customerDetails')}
                      size="small"
                    />
                  </Grid>

                  {/* Customer Name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Customer Name"
                      value={formData.customerName}
                      onChange={handleChange('customerName')}
                      size="small">
                      {customerName.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address}
                      onChange={handleChange('address')}
                      multiline
                      rows={2}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="h6" gutterBottom sx={{ mt: 1.5, fontWeight: 500 }}>
                      Volume & Pricing
                    </Typography>
                  </Grid>

                  {/* Estimated Volume */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Estimated Volume"
                      value={formData.estimatedVolume}
                      onChange={handleChange('estimatedVolume')}
                      size="small"
                    />
                  </Grid>

                  {/* Volume */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Volume"
                      value={formData.volume}
                      onChange={handleChange('volume')}
                      size="small"
                    />
                  </Grid>

                  {/* Measurement Type */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Measurement Type"
                      value={formData.measurementType}
                      onChange={handleChange('measurementType')}
                      placeholder="e.g., Tonnes, Cubic Meters"
                      size="small"
                    />
                  </Grid>

                  {/* Loads */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Loads"
                      value={formData.loads}
                      onChange={handleChange('loads')}
                      size="small"
                    />
                  </Grid>

                  {/* Haulage */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Haulage €"
                      value={formData.haulage}
                      onChange={handleChange('haulage')}
                      // error={!!errors.haulage}
                      // helperText={errors.haulage}
                      placeholder="Enter amount in euros"
                      size="small"
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                      <Button variant="outlined" onClick={resetForm} size="medium">
                        Reset
                      </Button>
                      <Button type="submit" variant="contained" size="medium" onClick={handleQuickContract}>
                        Submit Contract
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Box>
        </div>
      </div>
    </>
  );
}

export default QuickContract;
